// Settings › Sending › Mailbox and search health, "Ogma's agents" (job L-12, Find walk-through item 89; steps E3, E7). Reads
// crm_find_agent_health (migration 20261227120000) through crm.read, and puts into plain words, for this business, how
// each part of the engine that does Find's work ran over the last week and anything blocking one: a key that is missing
// or was refused, a mailbox that is not connected or is paused, or a cap that has been reached. Each blocker carries a
// link to the page that fixes it. Pure functions, so the wording is tested on its own. Never add methods to lib/crm.ts.
import type { Crm } from "./crm";
import { PART_TEXT } from "./engineSchedule";
import { CONNECT_MAILBOX_PATH, LIST_NAMES, fmtPct, type MailboxHealth, type MailboxHealthRow } from "./mailboxHealth";
import { money } from "./approvalsFind";
import { FINDER_LABELS, isLocked, statusWord, usd, type ContactFinderStats } from "./contactFinders";

/** The parts that do Find's work, in the page's order. Keep in step with crm__find_agent_parts(). */
export const FIND_AGENT_PARTS: { agent: string; job: string }[] = [
  { agent: "sourcing-agent", job: "daily" },
  { agent: "enrichment-agent", job: "daily" },
  { agent: "outreach-agent", job: "draft" },
  { agent: "outreach-agent", job: "daily" },
  { agent: "outreach-agent", job: "send" },
  { agent: "inbox-agent", job: "daily" },
];

export interface RunCounts {
  ok: number;
  partial: number;
  failed: number;
  skipped: number;
}

export interface AgentPart {
  agent: string;
  job: string;
  hub: string;
  scheduled: boolean;
  enabled: boolean;
  /** The part's area of the engine (find, know, reach, listen) is paused with an engine switch. */
  paused: boolean;
  last_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  failures: number;
  next_run_at: string | null;
  runs: RunCounts;
  last_failure: { at: string; status: string; error?: string; summary?: string } | null;
}

export interface AgentHealth {
  since: string | null;
  days: number;
  parts: AgentPart[];
}

/** Where each kind of fix lives. */
export const FIX_PATHS = {
  engine: "/settings/automations",
  mailboxes: CONNECT_MAILBOX_PATH,
  mailboxHealth: "/settings/sending#mailbox-health",
  dataSources: "/settings/data/sources",
  sendMore: "/settings/sending/more",
  spend: "/settings/approvals/outreach",
  phone: "/settings/modules/phone",
} as const;

export type BlockerKind = "key" | "mailbox" | "cap" | "failing" | "off" | "quiet";

export interface Blocker {
  key: string;
  kind: BlockerKind;
  /** What is wrong, in plain words. */
  text: string;
  /** The link's words. */
  fix: string;
  to: string;
  /** "bad" stops work; "warn" slows it or is the owner's own choice to look at. */
  tone: "bad" | "warn";
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/** Reads the function's answer tolerantly: a missing field never breaks the page. */
export function readAgentHealth(raw: unknown): AgentHealth {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rows = Array.isArray(o.parts) ? (o.parts as Record<string, unknown>[]) : [];
  const parts = rows
    .filter((r) => r && typeof r.agent === "string" && typeof r.job === "string")
    .map((r) => {
      const runs = (r.runs && typeof r.runs === "object" ? r.runs : {}) as Record<string, unknown>;
      const lf = r.last_failure && typeof r.last_failure === "object" ? (r.last_failure as Record<string, unknown>) : null;
      return {
        agent: String(r.agent),
        job: String(r.job),
        hub: str(r.hub) ?? "",
        scheduled: r.scheduled === true,
        enabled: r.enabled === true,
        paused: r.paused === true,
        last_run_at: str(r.last_run_at),
        last_status: str(r.last_status),
        last_error: str(r.last_error),
        failures: num(r.failures),
        next_run_at: str(r.next_run_at),
        runs: { ok: num(runs.ok), partial: num(runs.partial), failed: num(runs.failed), skipped: num(runs.skipped) },
        last_failure: lf ? { at: String(lf.at ?? ""), status: String(lf.status ?? "failed"), error: str(lf.error) ?? undefined, summary: str(lf.summary) ?? undefined } : null,
      };
    });
  return { since: str(o.since), days: num(o.days) || 7, parts };
}

/** 3 the card's read. */
export async function findAgentHealth(crm: Crm): Promise<AgentHealth> {
  return readAgentHealth(await crm.read<unknown>("crm_find_agent_health", {}));
}

/** "Finding firms", from Your engine's own names. */
export function partName(p: Pick<AgentPart, "agent" | "job">): string {
  return PART_TEXT[`${p.agent}/${p.job}`]?.name ?? `${p.agent.replace(/-agent$/, "")} (${p.job})`;
}

export function totalRuns(r: RunCounts): number {
  return r.ok + r.partial + r.failed + r.skipped;
}

/** "5 ok · 1 partly done · 2 failed · 1 skipped", or "No runs this week". */
export function runsText(r: RunCounts): string {
  if (!totalRuns(r)) return "No runs this week";
  return [
    r.ok ? `${r.ok} ok` : null,
    r.partial ? `${r.partial} partly done` : null,
    r.failed ? `${r.failed} failed` : null,
    r.skipped ? `${r.skipped} skipped` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The part's state in one word, for its chip. */
export function partState(p: AgentPart): { word: string; tone: "ok" | "warn" | "bad" | "muted" } {
  if (!p.scheduled || !p.enabled) return { word: "Off", tone: "muted" };
  if (p.paused) return { word: "Paused", tone: "warn" };
  if (p.runs.failed > 0 && (p.last_status === "failed" || p.last_status === "timeout")) return { word: "Failing", tone: "bad" };
  if (!totalRuns(p.runs)) return { word: "Not run", tone: "warn" };
  if (p.runs.failed > 0 || p.runs.partial > 0) return { word: "Mostly ran", tone: "warn" };
  return { word: "Ran", tone: "ok" };
}

const KEY_WORDS = /\b(401|403)\b|unauthori[sz]ed|forbidden|invalid[ _-]?(api[ _-]?)?key|api[ _-]?key|token (has )?expired|expired|revoked|credentials?/i;

const DISCONNECTED = /sign(ing)? in again|reconnect|disconnected|refresh[ _-]?token|invalid_grant|mailbox (is )?not connected/i;

/** Which page fixes a refused key, from the vendor the error names. */
export function keyFixPath(error: string): { to: string; fix: string } {
  const e = error.toLowerCase();
  if (/twilio|datasoap|\btps\b/.test(e)) return { to: FIX_PATHS.phone, fix: "Check the key in Phone" };
  if (/instantly/.test(e)) return { to: FIX_PATHS.sendMore, fix: "Reconnect Instantly" };
  if (/gmail|google|outlook|microsoft|smtp|imap|mailbox/.test(e)) return { to: FIX_PATHS.mailboxes, fix: "Reconnect the mailbox" };
  return { to: FIX_PATHS.dataSources, fix: "Check the key in Data sources" };
}

/**
 * Which page fixes a part that failed, from its error: a disconnected mailbox, a refused or expired key (keyFixPath),
 * else the part in Your engine. Used by the engine's state readers (lib/moduleAgents.ts).
 */
export function failureFix(error: string | null | undefined): { to: string; fix: string; kind: "mailbox" | "key" | "failing" } {
  const e = (error ?? "").replace(/\s+/g, " ").trim();
  if (DISCONNECTED.test(e)) return { to: FIX_PATHS.mailboxes, fix: "Reconnect the mailbox", kind: "mailbox" };
  if (KEY_WORDS.test(e)) return { ...keyFixPath(e), kind: "key" };
  return { to: FIX_PATHS.engine, fix: "See it in Your engine", kind: "failing" };
}

/** A failure's own words, short. */
function errorWords(p: AgentPart): string {
  const e = p.last_failure?.error ?? p.last_error ?? p.last_failure?.summary ?? "";
  const t = e.replace(/\s+/g, " ").trim();
  return t.length > 140 ? `${t.slice(0, 137)}…` : t;
}

/**
 * Everything blocking Find's agents, worst first: refused keys and disconnected or paused mailboxes stop work; caps
 * reached, failing parts, parts switched off or paused and parts that did not run this week come after.
 */
export function agentBlockers(a: AgentHealth | null, mail: MailboxHealth | null, finders: ContactFinderStats | null): Blocker[] {
  const out: Blocker[] = [];
  const add = (b: Blocker) => {
    if (!out.some((x) => x.key === b.key)) out.push(b);
  };

  // Mailboxes: none, none ready, paused for bounces, each paused one, every one at its daily limit.
  if (mail && Array.isArray(mail.mailboxes)) {
    const ready = mail.mailboxes.filter((m) => m.status === "ready");
    if (!mail.mailboxes.length) {
      add({ key: "mailbox-none", kind: "mailbox", tone: "bad", text: "No mailbox is connected, so approved emails cannot go out.", fix: "Connect a mailbox", to: FIX_PATHS.mailboxes });
    } else if (!ready.length) {
      add({ key: "mailbox-not-ready", kind: "mailbox", tone: "bad", text: "No mailbox is ready to send yet: each is still being set up.", fix: "Finish setting up a mailbox", to: FIX_PATHS.mailboxes });
    }
    if (mail.email_paused_by_bounces) {
      add({ key: "mailbox-bounces", kind: "mailbox", tone: "bad", text: "All email is paused because too many emails bounced.", fix: "Open Mailboxes", to: FIX_PATHS.mailboxHealth });
    }
    for (const m of mail.mailboxes.filter((x) => x.paused)) {
      const why = m.holds.some((h) => h.reason === "blocklist") ? "its domain is on a blocklist" : "of spam complaint signs";
      add({ key: `mailbox-paused-${m.mailbox_id}`, kind: "mailbox", tone: "bad", text: `${m.address} is paused because ${why}.`, fix: "Open Mailboxes", to: FIX_PATHS.mailboxHealth });
    }
    const sending = ready.filter((m) => !m.paused);
    if (sending.length && sending.every((m) => m.cap !== null && (m.remaining ?? m.cap - (m.sent_today ?? 0)) <= 0)) {
      add({ key: "cap-mailboxes", kind: "cap", tone: "warn", text: "Every mailbox has sent its daily limit. The rest go in the next 24 hours.", fix: "Send more each day", to: FIX_PATHS.sendMore });
    }
  }

  // Contact finders in use: a missing key, a finder's own cap, Find's contact finders cap. MillionVerifier's checks are
  // included on Ogma's central key (decision 016, #423), so they never need anything from the owner.
  if (finders) {
    for (const key of finders.order) {
      const f = finders.finders.find((x) => x.key === key);
      if (!f || isLocked(finders, key)) continue;
      const w = statusWord(finders, f);
      if (w === "Not connected") {
        add({ key: `key-${key}`, kind: "key", tone: "bad", text: `${FINDER_LABELS[key]} is one of your finders but has no API key.`, fix: "Add the key", to: FIX_PATHS.dataSources });
      } else if (w === "Cap reached") {
        add({ key: `cap-${key}`, kind: "cap", tone: "warn", text: `${FINDER_LABELS[key]} has reached its spending cap for this month (${usd(f.cap_month_usd)}).`, fix: "Change the cap", to: FIX_PATHS.spend });
      }
    }
    const pool = finders.spend_cap;
    if (pool && pool.cap > 0 && pool.over) {
      add({ key: "cap-finding", kind: "cap", tone: "warn", text: `Find's contact finders spending cap of ${money(pool.cap, pool.currency)} is reached this month, so paid finders wait until next month.`, fix: "Change the cap", to: FIX_PATHS.spend });
    }
  }

  // The parts themselves.
  for (const p of a?.parts ?? []) {
    const name = partName(p);
    const id = `${p.agent}/${p.job}`;
    if (!p.scheduled || !p.enabled) {
      add({ key: `off-${id}`, kind: "off", tone: "warn", text: `${name} is switched off, so it does not run.`, fix: "Switch it on in Your engine", to: FIX_PATHS.engine });
      continue;
    }
    if (p.paused) {
      add({ key: `paused-${id}`, kind: "off", tone: "warn", text: `${name} is paused with the rest of its area.`, fix: "Resume it in Your engine", to: FIX_PATHS.engine });
      continue;
    }
    const lastFailed = p.last_status === "failed" || p.last_status === "timeout";
    const err = errorWords(p);
    if (p.runs.failed > 0 && lastFailed && DISCONNECTED.test(err)) {
      add({ key: `mailbox-disconnected-${id}`, kind: "mailbox", tone: "bad", text: `${name} stopped because a mailbox is disconnected and needs signing in again.`, fix: "Reconnect the mailbox", to: FIX_PATHS.mailboxes });
    } else if (p.runs.failed > 0 && lastFailed && KEY_WORDS.test(err)) {
      const fx = keyFixPath(err);
      add({ key: `key-run-${id}`, kind: "key", tone: "bad", text: `${name} stopped because a key was refused or has expired${err ? `: "${err}"` : ""}.`, fix: fx.fix, to: fx.to });
    } else if (p.runs.failed > 0 && lastFailed) {
      const times = p.runs.failed === 1 ? "once" : p.runs.failed === 2 ? "twice" : `${p.runs.failed} times`;
      add({ key: `failing-${id}`, kind: "failing", tone: "bad", text: `${name} failed ${times} this week${err ? `: "${err}"` : ""}.`, fix: "See it in Your engine", to: FIX_PATHS.engine });
    } else if (!totalRuns(p.runs)) {
      add({ key: `quiet-${id}`, kind: "quiet", tone: "warn", text: `${name} has not run this week.`, fix: "Run it now in Your engine", to: FIX_PATHS.engine });
    }
  }

  const rank = (b: Blocker) => (b.tone === "bad" ? 0 : 1) * 10 + ["key", "mailbox", "failing", "cap", "quiet", "off"].indexOf(b.kind);
  return out.sort((x, y) => rank(x) - rank(y));
}

/** The card's subtitle: "All 6 ran this week · nothing blocking", "2 things blocking · 4 of 6 ran this week". */
export function agentsLine(a: AgentHealth, blockers: Blocker[]): string {
  const on = a.parts.filter((p) => p.scheduled && p.enabled && !p.paused);
  const ran = on.filter((p) => totalRuns(p.runs) > 0).length;
  const ranText = on.length === 0 ? "none switched on" : ran === on.length ? `all ${on.length} ran this week` : `${ran} of ${on.length} ran this week`;
  const bad = blockers.length;
  const lead = bad ? `${bad} ${bad === 1 ? "thing" : "things"} to fix` : "nothing blocking";
  return bad ? `${lead} · ${ranText}` : `${ranText.charAt(0).toUpperCase()}${ranText.slice(1)} · ${lead}`;
}

// ------------------------------------------------------------------ Mailbox health, read only (the Health page's card)

/** Spam complaints, bounces and blocklists for one mailbox, each a plain line and how bad it is. */
export function mailboxSignals(m: MailboxHealthRow, h: MailboxHealth): { label: string; value: string; tone: "ok" | "warn" | "bad" }[] {
  const s = h.settings;
  const listed = m.blocklists.filter((b) => b.listed);
  const checked = m.blocklists.length > 0;
  const bounceOver = h.bounce_sent > 0 && h.bounce_pct !== null && h.bounce_pct > s.bounce_pct;
  const bounceNear = h.bounce_sent > 0 && h.bounce_pct !== null && h.bounce_pct > s.bounce_pct / 2;
  return [
    {
      label: "Spam complaints",
      value: m.complaints ? `${m.complaints} in ${m.sends} emails` : "None",
      tone: m.complaints ? "bad" : "ok",
    },
    {
      label: `Bounces (last ${s.bounce_window_days} days, all mailboxes)`,
      value: h.bounce_sent ? `${fmtPct(h.bounce_pct ?? 0)} (stops at ${fmtPct(s.bounce_pct)})` : "No emails yet",
      tone: bounceOver ? "bad" : bounceNear ? "warn" : "ok",
    },
    {
      label: "Blocklists",
      value: listed.length
        ? listed.map((b) => `Listed on ${LIST_NAMES[b.provider] ?? b.provider}`).join("; ")
        : checked
          ? "Not listed"
          : "Not checked yet",
      tone: listed.length ? "bad" : "ok",
    },
  ];
}

/** What to do about it, in plain words; empty when there is nothing to do. */
export function mailboxFixes(m: MailboxHealthRow, h: MailboxHealth): string[] {
  const s = h.settings;
  const out: string[] = [];
  if (m.complaints > 0) {
    out.push("Someone marked an email as spam. Write only to firms that fit, say in the first line why you are writing to them, and make it easy to say no.");
  }
  if (h.bounce_sent > 0 && h.bounce_pct !== null && h.bounce_pct > s.bounce_pct / 2) {
    out.push("Too many emails bounce. Use the addresses Ogma finds and checks, and don't import old lists.");
  }
  const listed = m.blocklists.filter((b) => b.listed);
  if (listed.length) {
    const names = listed.map((b) => LIST_NAMES[b.provider] ?? b.provider).join(" and ");
    out.push(`Ask ${names} to remove ${m.domain} using its removal form. Ogma checks again every day and starts sending again once the domain is clear.`);
  }
  if (m.paused && !listed.length) {
    out.push("Sending from this mailbox is paused. Look at the replies and unsubscribes that set it off, then resume it in Mailboxes.");
  }
  if (overHalf(m.unsubscribe_pct, s.unsubscribe_pct, m.judged) || overHalf(m.negative_pct, s.negative_pct, m.judged)) {
    out.push("More people than usual ask to stop. Check who your campaigns target and soften the first email.");
  }
  return out;
}

function overHalf(pct: number | null, limit: number, judged: boolean): boolean {
  return judged && pct !== null && pct > limit / 2;
}

/** The mailbox card's subtitle: "2 mailboxes · all clear", "1 of 2 paused · 1 spam complaint". */
export function mailboxesLine(h: MailboxHealth | null): string {
  if (!h) return "Couldn't read your mailboxes";
  const n = h.mailboxes.length;
  if (!n) return "No mailbox connected yet";
  const paused = h.mailboxes.filter((m) => m.paused).length;
  const complaints = h.mailboxes.reduce((x, m) => x + m.complaints, 0);
  const listed = h.mailboxes.filter((m) => m.blocklists.some((b) => b.listed)).length;
  const parts: string[] = [];
  if (h.email_paused_by_bounces) parts.push("all email paused by bounces");
  else if (paused) parts.push(`${paused} of ${n} paused`);
  if (complaints) parts.push(`${complaints} spam ${complaints === 1 ? "complaint" : "complaints"}`);
  if (listed) parts.push(`${listed} on a blocklist`);
  const head = `${n} ${n === 1 ? "mailbox" : "mailboxes"}`;
  return parts.length ? `${head} · ${parts.join(" · ")}` : `${head} · all clear`;
}
